import { useEffect, useMemo, useRef, useState } from 'react';

// Leaflet is loaded on-demand from unpkg so the portal doesn't have to bundle
// it as an npm dependency. The browser caches both assets after the first
// dashboard visit, so the network cost is paid once.
const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

// Brand colors used by the custom map markers. Kept in sync with the
// `--brand-primary` CSS variable defined in theme.css.
const BRAND_PRIMARY = '#E87722';
const BRAND_PRIMARY_DARK = '#B85D16';

// Nominatim is the free public OpenStreetMap geocoder. No API key required,
// but it rate-limits to ~1 request per second and asks that we identify the
// app via the Referer (which the browser sends automatically). We cache
// results in localStorage so re-opening the dashboard doesn't re-geocode.
const GEOCODE_BASE = 'https://nominatim.openstreetmap.org/search';
const GEOCODE_CACHE_KEY = 'proxemics-portal:geocode-cache';
const GEOCODE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function loadGeocodeCache() {
  try {
    const raw = localStorage.getItem(GEOCODE_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function saveGeocodeCache(cache) {
  try {
    localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota / disabled-storage errors
  }
}

function buildAddressString(p) {
  return [p.AddressStreet, p.City, p.State, p.PostalCode, p.Country]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(', ');
}

// Coarser, city-level address used as a geocoding fallback. Large
// assembly complexes often aren't in OpenStreetMap at the exact street
// level, but "City, State, ZIP, Country" almost always resolves — so a
// plant lands a (slightly approximate) pin instead of vanishing.
function buildCityState(p) {
  return [p.City, p.State, p.PostalCode, p.Country]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(', ');
}

// Fetch one Nominatim lookup, returning a {lat, lon} record or null.
// Retries once on a network/HTTP hiccup so a single transient blip
// doesn't drop a plant for the whole session.
async function geocodeOnce(query) {
  const url = `${GEOCODE_BASE}?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data) && data[0]) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
          return { lat, lon, ts: Date.now() };
        }
      }
      return null; // valid response, just no match — no point retrying
    } catch {
      // transient error — pause briefly and try once more
      if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
    }
  }
  return null;
}

// Inject the small CSS overrides the map needs — killing Leaflet's default
// DivIcon white box/border so our SVG pin sits on the map cleanly, and
// tinting popup links with the brand orange. Idempotent.
function ensureMapStyles() {
  if (typeof document === 'undefined') return;
  if (document.querySelector('style[data-proxemics-map]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-proxemics-map', '1');
  style.textContent = `
    .proxemics-pin { background: transparent; border: none; }
    .proxemics-pin svg { display: block; filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.35)); }
    .leaflet-popup-content a { color: ${BRAND_PRIMARY}; text-decoration: none; font-weight: 500; }
    .leaflet-popup-content a:hover { text-decoration: underline; }
  `;
  document.head.appendChild(style);
}

// Dynamically inject Leaflet's CSS + JS. Promise-cached so concurrent
// renders (e.g. role-based Dashboard variants) don't add the script twice.
let leafletPromise = null;
function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.setAttribute('data-leaflet', '1');
      document.head.appendChild(link);
    }
    const existing = document.querySelector('script[data-leaflet]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', () => reject(new Error('Leaflet load failed')));
      return;
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.setAttribute('data-leaflet', '1');
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Leaflet load failed'));
    document.head.appendChild(script);
  });
  return leafletPromise;
}

// Walk the geocode items one at a time (Nominatim's 1 req/sec rule), using
// the cache when possible. Each item carries an ordered list of query
// strings: the full street address first, then a coarser city-level
// fallback. We try them in order and keep the first hit, so a plant whose
// exact street isn't in OpenStreetMap still lands a city-level pin instead
// of being dropped. The `onTick` hook lets the caller redraw partial
// results as markers arrive.
async function geocodeSequentially(items, cache, onTick) {
  const results = {};
  for (const item of items) {
    const { key, queries } = item;
    if (!key) continue;
    const cached = cache[key];
    if (cached && typeof cached.lat === 'number' && typeof cached.lon === 'number' &&
        Date.now() - (cached.ts || 0) < GEOCODE_TTL_MS) {
      results[key] = cached;
      onTick?.(key, cached);
      continue;
    }
    // Try each query (full address, then city-level fallback) until one
    // resolves. Pause politely between every network request.
    for (const q of queries) {
      if (!q) continue;
      const rec = await geocodeOnce(q);
      await new Promise((r) => setTimeout(r, 1100));
      if (rec) {
        results[key] = rec;
        cache[key] = rec;
        onTick?.(key, rec);
        break;
      }
    }
  }
  saveGeocodeCache(cache);
  return results;
}

// Build an inline-SVG pin styled in the brand orange. Using a DivIcon lets
// us skip the shipped PNG marker entirely — no broken default-icon paths
// and the pin visually matches the rest of the portal.
function brandPinIcon(L) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path d="M14 0 C6.27 0 0 6.27 0 14 c0 10.5 14 26 14 26 s14 -15.5 14 -26 C28 6.27 21.73 0 14 0 z"
            fill="${BRAND_PRIMARY}" stroke="${BRAND_PRIMARY_DARK}" stroke-width="1.5" />
      <circle cx="14" cy="14" r="5" fill="#ffffff" />
    </svg>`;
  return L.divIcon({
    className: 'proxemics-pin',
    html: svg,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Dashboard map card. Plots every plant with a usable address, using
 * OpenStreetMap tiles + Nominatim geocoding. Lives in the Dashboard below
 * the News & Announcements + Quick Actions row.
 */
export default function PlantsMap({ plants }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const cacheRef = useRef(loadGeocodeCache());
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [unlocated, setUnlocated] = useState([]);

  // Plants that at least have something to geocode.
  const plantsWithAddress = useMemo(
    () => (plants || []).filter((p) => buildAddressString(p)),
    [plants],
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setStatus('loading');
      ensureMapStyles();
      let L;
      try {
        L = await loadLeaflet();
      } catch {
        if (!cancelled) setStatus('error');
        return;
      }
      if (cancelled || !L || !containerRef.current) return;

      // One-time map init. Default view is roughly centred on North America
      // until fitBounds kicks in below. We use CartoDB's "Positron" tile
      // set — a light gray basemap that matches the portal's neutral
      // aesthetic, so the orange pins are the only thing drawing the eye.
      if (!mapRef.current) {
        const map = L.map(containerRef.current, {
          scrollWheelZoom: false,
        }).setView([39.5, -98.35], 4);
        // CARTO now requires a free API key on its basemap tiles (Oct
        // 2026); without one every tile is stamped "API KEY REQUIRED".
        // The key lives in VITE_CARTO_API_KEY (.env.local). Until it's
        // set, fall back to the standard OpenStreetMap tiles, turned gray
        // with a CSS filter so the map keeps the same neutral look.
        const cartoKey = String(import.meta.env.VITE_CARTO_API_KEY || '').trim();
        if (cartoKey) {
          L.tileLayer(
            `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoKey)}`,
            {
              attribution:
                '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, © <a href="https://carto.com/attributions">CARTO</a>',
              subdomains: 'abcd',
              maxZoom: 19,
            },
          ).addTo(map);
        } else {
          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution:
              '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
            className: 'plants-map-gray-tiles',
          }).addTo(map);
          if (!document.getElementById('plants-map-gray-style')) {
            const st = document.createElement('style');
            st.id = 'plants-map-gray-style';
            st.textContent =
              '.plants-map-gray-tiles{filter:grayscale(1) brightness(1.08) contrast(0.9);}';
            document.head.appendChild(st);
          }
        }
        mapRef.current = map;
      }

      // Clear any previous markers when the plants list changes.
      if (markersLayerRef.current) {
        markersLayerRef.current.remove();
      }
      const layer = L.layerGroup().addTo(mapRef.current);
      markersLayerRef.current = layer;

      if (plantsWithAddress.length === 0) {
        setUnlocated([]);
        setStatus('ready');
        return;
      }

      // Map each full-address key to the plant(s) at that address, and
      // build the deduped geocode items. Each item tries the full street
      // address first, then a coarser City/State/ZIP fallback so plants
      // whose exact street isn't in OpenStreetMap still get placed.
      const addrToPlants = new Map();
      const itemsByKey = new Map();
      plantsWithAddress.forEach((p) => {
        const key = buildAddressString(p);
        const arr = addrToPlants.get(key) || [];
        arr.push(p);
        addrToPlants.set(key, arr);
        if (!itemsByKey.has(key)) {
          const fallback = buildCityState(p);
          const queries = fallback && fallback !== key ? [key, fallback] : [key];
          itemsByKey.set(key, { key, queries });
        }
      });
      const geoItems = Array.from(itemsByKey.values());

      const placedPoints = [];
      const placedAddresses = new Set();

      const pinIcon = brandPinIcon(L);
      const handleTick = (addr, coord) => {
        if (cancelled) return;
        const group = addrToPlants.get(addr) || [];
        group.forEach((p) => {
          const link = `/plants/${encodeURIComponent(p.PlantCode || '')}`;
          const popupHtml =
            `<strong>${escapeHtml(p.Title || p.PlantCode || 'Plant')}</strong><br/>` +
            `${escapeHtml(buildAddressString(p))}<br/>` +
            `<a href="${link}">View Plant →</a>`;
          L.marker([coord.lat, coord.lon], { icon: pinIcon })
            .addTo(layer)
            .bindPopup(popupHtml);
        });
        placedPoints.push([coord.lat, coord.lon]);
        placedAddresses.add(addr);
        if (mapRef.current && placedPoints.length > 0) {
          mapRef.current.fitBounds(placedPoints, {
            padding: [30, 30],
            maxZoom: 10,
          });
        }
      };

      await geocodeSequentially(geoItems, cacheRef.current, handleTick);
      if (cancelled) return;

      const missed = plantsWithAddress.filter(
        (p) => !placedAddresses.has(buildAddressString(p)),
      );
      setUnlocated(missed);
      setStatus('ready');
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [plantsWithAddress]);

  // Tear down the Leaflet map when the card unmounts so we don't leak
  // listeners if the user navigates away mid-geocode.
  useEffect(
    () => () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    },
    [],
  );

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          height: 360,
          width: '100%',
          borderRadius: 6,
          border: '1px solid var(--brand-border, #e5e7eb)',
          background: '#f7f8fa',
        }}
      />
      {status === 'loading' && plantsWithAddress.length > 0 && (
        <p className="muted tiny" style={{ marginTop: 8 }}>
          Loading map and placing plants… (first-time lookups can take a few
          seconds per plant)
        </p>
      )}
      {status === 'error' && (
        <p className="muted tiny" style={{ marginTop: 8 }}>
          Map couldn't load. Check your internet connection and try again.
        </p>
      )}
      {status === 'ready' && plantsWithAddress.length === 0 && (
        <p className="muted tiny" style={{ marginTop: 8 }}>
          No plant addresses to map yet.
        </p>
      )}
      {status === 'ready' && unlocated.length > 0 && (
        <p className="muted tiny" style={{ marginTop: 8 }}>
          Couldn't locate:{' '}
          {unlocated.map((p) => p.Title || p.PlantCode).join(', ')}
        </p>
      )}
    </div>
  );
}
